import React, { useRef, useState } from 'react';
import {
  IonModal,
  IonContent,
  IonButton,
  IonCheckbox,
  IonIcon,
} from '@ionic/react';
import { useAppToast } from '@/hooks/useAppToast';
import { documentOutline } from 'ionicons/icons';
import { useSuppressibleNotices } from '@/contexts/SuppressibleNoticesContext';
import {
  parseVCardFile,
  useContactState,
  useImportToast,
} from '@/modules/contact';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import {
  INTERSTITIAL_BREAKPOINT,
  INTERSTITIAL_BREAKPOINTS,
  pxToRem,
} from '@/modules/config';
import '@/components/Interstitial.css';

interface ContactsInterstitialProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const ContactsInterstitial: React.FC<ContactsInterstitialProps> = ({
  isOpen,
  onDismiss,
}) => {
  const { suppress, unsuppress } = useSuppressibleNotices();
  const { isIOS } = useDeviceInfo();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { dispatch } = useContactState();
  const [presentToast] = useAppToast();
  const presentImportToast = useImportToast();

  const expandToAnchor = async (anchorId: string) => {
    await modalRef.current?.setCurrentBreakpoint(1);
    setTimeout(() => {
      const el = document.getElementById(anchorId);
      if (el) {
        contentRef.current?.scrollToPoint(0, el.offsetTop - 16, 300);
      }
    }, 300);
  };

  const handleDismiss = () => {
    onDismiss();
  };

  const handleCheckboxChange = (checked: boolean) => {
    setDontShowAgain(checked);
    if (checked) suppress('contacts');
    else unsuppress('contacts');
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.vcf')) {
      await presentToast({
        message: 'Please select a .vcf file',
        duration: 2000,
        color: 'warning',
      });
      return;
    }

    try {
      const imported = await parseVCardFile(file);
      if (imported.length === 0) {
        await presentToast({
          message: 'No valid contacts found in file. Please check the format.',
          duration: 3000,
          color: 'warning',
        });
        return;
      }
      dispatch({ type: 'PROVISIONALLY_ADD_CONTACTS', payload: imported });
      setSelectedFile(file);
      presentImportToast(
        imported.length === 1
          ? '1 contact imported'
          : `${imported.length} contacts imported`
      );
    } catch {
      await presentToast({
        message: 'Failed to import contacts. Please check the file format.',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      initialBreakpoint={INTERSTITIAL_BREAKPOINT}
      breakpoints={INTERSTITIAL_BREAKPOINTS}
      handle
      onDidDismiss={handleDismiss}
    >
      <IonContent ref={contentRef} className="ion-padding-top">
        <p className="interstitial__message">
          The next screen lets you pick who to share this topic with. We
          recommend you take a moment now to{' '}
          <button
            className="interstitial__link"
            onClick={() => expandToAnchor('contacts-vcf')}
          >
            import entries
          </button>{' '}
          from your device&apos;s Contacts app into your Snowball.{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            (
            <button
              className="interstitial__link"
              onClick={() => expandToAnchor('contacts-convince')}
            >
              convince me
            </button>
            )
          </span>
        </p>

        <div className="interstitial__checkbox-row">
          <IonCheckbox
            checked={dontShowAgain}
            onIonChange={e => handleCheckboxChange(e.detail.checked)}
          />
          <span
            style={{ fontSize: pxToRem(14), color: 'var(--ion-color-medium)' }}
          >
            Got it!
          </span>
        </div>

        <IonButton
          expand="block"
          className="interstitial__continue-btn"
          onClick={handleDismiss}
        >
          {selectedFile ? 'Continue' : 'Skip importing'}
        </IonButton>

        {/* Detail zone */}
        <hr className="interstitial__section-rule" />

        <div id="contacts-vcf">
          <p className="interstitial__section-heading">Import contacts</p>
          <ol
            className="interstitial__body"
            style={{ paddingLeft: 36, paddingTop: 0 }}
          >
            {isIOS ? (
              <>
                <li>Switch to the Contacts app</li>
                <li>
                  Tap on <strong>Lists</strong> in the top left corner
                </li>
                <li>Long press on the list you want to export</li>
                <li>
                  Choose <strong>Export</strong>
                </li>
                <li>
                  Choose <strong>Save to Files</strong>
                </li>
                <li>
                  Tap on <strong>Save</strong> in the top right corner
                </li>
                <li>Return to the Snowball app</li>
                <li>
                  Tap on the <strong>Import</strong> button below
                </li>
                <li>Select the .vcf file you just exported</li>
              </>
            ) : (
              <>
                <li>Switch to the Contacts app</li>
                <li>
                  Tap <strong>Organize</strong> on the toolbar
                </li>
                <li>
                  Scroll down and tap <strong>Export to file</strong>
                </li>
                <li>
                  Tap <strong>Export</strong>
                </li>
                <li>
                  Tap <strong>SAVE</strong>
                </li>
                <li>Return to the Snowball app</li>
                <li>
                  Tap on the <strong>Import</strong> button below
                </li>
                <li>Select the .vcf file you just exported</li>
              </>
            )}
          </ol>

          <input
            ref={fileInputRef}
            type="file"
            accept=".vcf"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <div style={{ padding: '0 16px 12px' }}>
            <IonButton
              expand="block"
              color="secondary"
              onClick={() => fileInputRef.current?.click()}
            >
              <IonIcon icon={documentOutline} slot="start" />
              {selectedFile ? selectedFile.name : 'Import VCF File'}
            </IonButton>
          </div>
          <p className="interstitial__body">
            For more address book options see your Profile page
          </p>
        </div>

        <hr className="interstitial__section-rule" />

        <div id="contacts-convince">
          <p className="interstitial__section-heading">Why pre-populate?</p>
          <p className="interstitial__body">
            A quick one-time import now saves you that friction every time you
            want to share an topic. Note: Your address book is stored on your
            device, with an encrypted backup kept on Snowball&apos;s servers so
            you don&apos;t lose it if your device clears its storage. It is
            encrypted on your device before upload and is never shared with
            third parties.
          </p>
        </div>

        <div style={{ height: 40 }} />
      </IonContent>
    </IonModal>
  );
};

export default ContactsInterstitial;
